'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import { ExternalLink } from 'lucide-react';
import {
  Table, TableHead, TableBody, TableRow, TableHeader, TableCell,
  Skeleton, SkeletonAvatar, EmptyState, Tooltip,
  SideSheet, SideSheetPortal, SideSheetBackdrop, SideSheetContent, SideSheetBody, SideSheetClose,
} from '@/ui';
import iconButtonStyles from '@/ui/components/IconButton.module.css';
import { JobStatus, PEOPLE_SORT_OPTIONS } from '@lib/enums';
import mapCandidatesToImportStatus from '@lib/candidates/mapCandidatesToImportStatus';
import Candidate from './Candidate/Candidate';
import CandidateRow from './parts/CandidateRow';
import ImportingRow from './parts/ImportingRow';
import FailedRow from './parts/FailedRow';

const SKELETON_ROWS = 5;

export default function CandidateList({ candidates, jobs, sortBy, setSortBy, isLoading }) {
  const statusMap = mapCandidatesToImportStatus(jobs ?? []);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [actionsContainer, setActionsContainer] = useState(null);

  // The open sheet lives in the URL so it's shareable, survives a reload, and
  // closes on Back instead of dropping the user out of the list entirely.
  const openCandidateUuid = searchParams.get('candidate');

  // Whether *we* pushed the entry we're sitting on. If the user arrived on a
  // ?candidate= link directly there's nothing to go back to, so closing has to
  // strip the param instead of walking out of the app.
  const pushedRef = useRef(false);

  const buildHref = useCallback(
    (mutate) => {
      const params = new URLSearchParams(searchParams);
      mutate(params);
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [pathname, searchParams]
  );

  const openProfile = (candidate) => {
    pushedRef.current = true;
    router.push(buildHref((params) => params.set('candidate', candidate.uuid)), {
      scroll: false,
    });
  };

  // Dismissing via the browser's own Back button closes the sheet without
  // going through closeProfile, so clear the flag whenever it ends up shut.
  useEffect(() => {
    if (!openCandidateUuid) pushedRef.current = false;
  }, [openCandidateUuid]);

  const closeProfile = useCallback(() => {
    if (pushedRef.current) {
      pushedRef.current = false;
      router.back();
      return;
    }
    router.replace(buildHref((params) => params.delete('candidate')), { scroll: false });
  }, [buildHref, router]);

  const handleSortBy = (property) => {
    if (sortBy.property === property) {
      setSortBy({ property, direction: sortBy.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSortBy({ property, direction: 'asc' });
    }
  };

  const getSortDirection = (property) =>
    sortBy.property !== property ? null : sortBy.direction;

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table hoverable style={{ tableLayout: 'fixed', width: '100%', minWidth: 798 }}>
        <TableHead>
          <TableRow>
            <TableHeader style={{ width: 40 }} />
            <TableHeader
              sortable
              sortDirection={getSortDirection(PEOPLE_SORT_OPTIONS.NAME)}
              onSort={() => handleSortBy(PEOPLE_SORT_OPTIONS.NAME)}
              style={{ width: 250 }}
            >
              Name
            </TableHeader>
            <TableHeader>Skills</TableHeader>
            <TableHeader
              sortable
              sortDirection={getSortDirection(PEOPLE_SORT_OPTIONS.UPDATED_AT)}
              onSort={() => handleSortBy(PEOPLE_SORT_OPTIONS.UPDATED_AT)}
              style={{ width: 120 }}
            >
              Updated
            </TableHeader>
            <TableHeader style={{ width: 90, textAlign: 'right' }} />
            <TableHeader style={{ width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><SkeletonAvatar size="sm" /></TableCell>
                <TableCell><Skeleton width={150} height={13} /></TableCell>
                <TableCell><Skeleton width={140} height={13} /></TableCell>
                <TableCell><Skeleton width={70} height={13} /></TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
            ))
          ) : !candidates?.length ? (
            <TableRow>
              <TableCell colSpan={6} style={{ padding: '24px 16px' }}>
                <EmptyState
                  icon="🔍"
                  title="No candidates found"
                  description="Try adjusting your search or filters to find what you're looking for."
                />
              </TableCell>
            </TableRow>
          ) : (
            candidates.map((candidate) => {
              const status = statusMap.get(candidate.id);
              return status === JobStatus.IMPORTING_CV ? (
                <ImportingRow key={candidate.id} candidate={candidate} />
              ) : status === JobStatus.FAILED ? (
                <FailedRow key={candidate.id} candidate={candidate} />
              ) : (
                <CandidateRow key={candidate.id} candidate={candidate} status={status} openProfile={openProfile} />
              );
            })
          )}
        </TableBody>
      </Table>
      <SideSheet
        open={Boolean(openCandidateUuid)}
        onOpenChange={(open) => {
          if (!open) {
            closeProfile();
          }
        }}
      >
        <SideSheetPortal>
          <SideSheetBackdrop />
          <SideSheetContent size="lg">
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-5)',
                background: 'var(--action-primary-subtle)',
              }}
            >
              <div ref={setActionsContainer} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                {openCandidateUuid && (
                  <Tooltip content="Open in new tab">
                    <a
                      href={`/candidates/${openCandidateUuid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={clsx(iconButtonStyles.iconButton)}
                      style={{
                        width: 'auto',
                        height: '32px',
                        padding: '0 var(--space-3)',
                        gap: 'var(--space-2)',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                      }}
                    >
                      Open full view
                      <ExternalLink size={16} />
                    </a>
                  </Tooltip>
                )}
                <SideSheetClose style={{ position: 'static' }} />
              </div>
            </div>
            <SideSheetBody>
              {openCandidateUuid && (
                <Candidate
                  candidateId={openCandidateUuid}
                  hideBackButton
                  actionsContainer={actionsContainer}
                />
              )}
            </SideSheetBody>
          </SideSheetContent>
        </SideSheetPortal>
      </SideSheet>
    </div>
  );
}
